import { BULK_PLUGIN_VERSION } from '../views/BulkEnrich/constants';
import { isVersionGreaterThen } from './comparePluginVersion';
/** The kind of page the user is on, as derived from its url by `getType`. */
export const PAGE_TYPES = {
    UNKNOWN: 'UNKNOWN',
    PROFILE: 'PROFILE',
    BULK: 'BULK',
    SALESFORCE_ACCOUNT: 'SALESFORCE_ACCOUNT',
    HUBSPOT_COMPANY: 'HUBSPOT_COMPANY',
    LINKEDIN_COMPANY: 'LINKEDIN_COMPANY',
};
const HUBSPOT_PLUGIN_VERSION = '10.3.7';
const RECRUITER_PAGES_VERSION = '10.3.12';
const isLinkedinRegularProfilePage = url => url.includes('linkedin.com/in/') && !url.includes('linkedin.com/in/unavailable');
const isSalesNavigatorProfilePage = url => url.includes('linkedin.com/sales/people') ||
    url.includes('linkedin.com/sales/profile') ||
    url.includes('linkedin.com/sales/lead');
const isRecruiterProfilePage = url => url.includes('linkedin.com/talent/home/reminders/profile') ||
    url.includes('linkedin.com/recruiter/profile') ||
    url.includes('linkedin.com/talent/profile') ||
    (((url.includes('linkedin.com/talent/hire') && url.includes('/profile')) ||
        url.includes('linkedin.com/talent/search')) &&
        url.includes('/profile'));
const isLinkedinProfilePage = url => (isLinkedinRegularProfilePage(url) || isSalesNavigatorProfilePage(url) || isRecruiterProfilePage(url)) &&
    // LinkedIn Regular nested tabs
    !url.includes('/details/') &&
    !url.includes('/recent-activity') &&
    // Recruiter nested tabs
    !url.includes('/requisition') &&
    !url.includes('/feedback') &&
    !url.includes('/messages') &&
    !url.includes('/attachments') &&
    !url.includes('/recruiting-activities') &&
    !url.includes('/messages');
const isSalesforceLigthningLeadPage = url => {
    const leadViewUrlRegex = /lead\/\w*\/view/i;
    return leadViewUrlRegex.test(url);
};
const isSalesforceLigthningContactPage = url => {
    const contactViewUrlRegex = /contact\/\w*\/view/i;
    return contactViewUrlRegex.test(url);
};
const isSalesNavigatorBulkPage = url => url && url.includes('linkedin.com/sales/search/people');
const isSalesNavigatorLeadsPage = url => url && url.includes('linkedin.com/sales/lists/people/');
const isLinkedInPeopleSearchPage = url => url && url.includes('linkedin.com/search/results/people');
const isRecruiterBulkPage = (url, corePluginVersion) => !!(url &&
    (url.includes('linkedin.com/talent/search') ||
        (url.includes('linkedin.com/talent/hire') && isVersionGreaterThen(corePluginVersion, RECRUITER_PAGES_VERSION))));
export const isLinkedinSearchPage = url => url && url.includes('linkedin.com/search/results/');
export const isHubspotProfilePage = url => url && url.includes('hubspot.com/contacts/') && (url.includes('/contact/') || url.includes('/record/0-1'));
export const isHubspotCompanyPage = url => url && url.includes('hubspot.com/contacts/') && (url.includes('/company/') || url.includes('/record/0-2'));
export const isLinkedinProductsCompanyPage = url => url && (url.includes('linkedin.com/company/') || url.includes('linkedin.com/sales/company/'));
export const isSalesforceAccountPage = url => {
    const contactViewUrlRegex = /account\/\w*\/view/i;
    return contactViewUrlRegex.test(url);
};
const isBulk = ({ url, corePluginVersion }) => isSalesNavigatorBulkPage(url) ||
    isSalesNavigatorLeadsPage(url) ||
    isLinkedInPeopleSearchPage(url) ||
    isRecruiterBulkPage(url, corePluginVersion);
const isGmailContactPage = url => {
    if (url.includes('mail.google.com')) {
        const queryStringStart = url.indexOf('#inbox');
        const suffixUrl = url.substring(queryStringStart);
        if (suffixUrl !== '#inbox') {
            // if suffix is not equal to '#inbox' means we are inside an email
            return true;
        }
    }
    return false;
};
const enableHubspotProfileParser = (url, currentVersion) => isHubspotProfilePage(url) && isVersionGreaterThen(currentVersion, HUBSPOT_PLUGIN_VERSION);
const isProfile = (url, currentVersion) => url &&
    (isGmailContactPage(url) ||
        isLinkedinProfilePage(url) ||
        isSalesforceLigthningContactPage(url) ||
        isSalesforceLigthningLeadPage(url) ||
        enableHubspotProfileParser(url, currentVersion));
export const getType = ({ url, corePluginVersion, type, }) => {
    if (type === PAGE_TYPES.PROFILE || isProfile(url, corePluginVersion)) {
        return PAGE_TYPES.PROFILE;
    }
    if (isBulk({ url, corePluginVersion }) && isVersionGreaterThen(corePluginVersion, BULK_PLUGIN_VERSION)) {
        return PAGE_TYPES.BULK;
    }
    if (type === PAGE_TYPES.SALESFORCE_ACCOUNT || isSalesforceAccountPage(url)) {
        return PAGE_TYPES.SALESFORCE_ACCOUNT;
    }
    if ((type === PAGE_TYPES.HUBSPOT_COMPANY || isHubspotCompanyPage(url)) &&
        isVersionGreaterThen(corePluginVersion, HUBSPOT_PLUGIN_VERSION)) {
        return PAGE_TYPES.HUBSPOT_COMPANY;
    }
    if (isLinkedinProductsCompanyPage(url)) {
        return PAGE_TYPES.LINKEDIN_COMPANY;
    }
    return PAGE_TYPES.UNKNOWN;
};
const SLUG_PREFIX_REGEX = '^\\/[a-z0-9]+(?:-[a-z0-9]+)*\\/[a-z0-9]+(?:-[a-z0-9]+)*\\/';
/**
 * Platforms that nest sub-views under a profile url (LinkedIn Recruiter, Sales Navigator) re-fire
 * `PAGE` as the user moves between tabs of the same profile. Those are not new pages to search.
 */
export const shouldPreventSearchesOnSubViews = (currentPage, lastURL) => {
    var _a, _b;
    const pathName = new URL(currentPage.url).pathname;
    const basePathName = (_a = pathName.match(SLUG_PREFIX_REGEX)) === null || _a === void 0 ? void 0 : _a[0];
    return (lastURL === null || lastURL === void 0 ? void 0 : lastURL.includes(basePathName)) && ((_b = currentPage.url) === null || _b === void 0 ? void 0 : _b.includes(basePathName));
};
